// Pure helper that flattens an Orion reporting/scope response carrying
// one OR MORE `*-series` calcs into a map keyed by the request's calc
// id (`trend_0`, `trend_1`, ...). The trend tool's multi-series support
// piggybacks on Orion echoing the request's `id` back on each
// `calculated-*-series` node.
//
// Walk strategy:
//   - Without `unwrap`: collect every `calculated-*-series` node found
//     anywhere in the tree (level scope). Calcs sit at the top level
//     or inside benchmark Comparison wraps -- both are reachable via
//     recursive descent.
//   - With `unwrap`: virtual-grouping row scope wraps the calcs in a
//     filter grouping, but Orion's `string-equal` filter on
//     `group-info` doesn't actually constrain the response -- the
//     wrapper still returns every sibling group at that grouping
//     level. Find the matching group via the shared
//     `findGroupByIdentityInTree` (id-then-name semantics: when
//     `groupEntityId` is provided, do not fall back to name; that
//     would conflate homonyms across siblings), THEN collect every
//     series inside it. Mirrors fetchRowChildren's unwrap in
//     usePortfolioExplorerQuery.js.

import { findGroupByIdentityInTree } from '../../lib/findRowByIdentity'

const SERIES_TYPES = new Set([
  'calculated-performance-series',
  'calculated-activity-series',
  'calculated-benchmark-performance-series',
  // marketValue-series ($type alias varies across Orion versions; both
  // current production names are accepted).
  'calculated-market-value-series'
  // Note: `calculated-benchmark-aftertax-performance-series` is NOT
  // listed here because no `CALC_SOURCES` entry emits a
  // benchmarkAftertaxPerformanceSeries calc today --
  // `performance.seriesCounterpart` always returns `performanceSeries`
  // (after-tax rides via contextChange on the same calc). To wire up
  // the actual after-tax-benchmark series, see PR_FOLLOWUPS.md
  // ("Wire up benchmark-aftertax-performance-series").
])

// Each Orion *-series response uses a different value field. Map series
// $type -> the value field on each entry. Unknown types fall back to
// reading `entry.value` (most calcs use this).
const SERIES_VALUE_FIELD = {
  'calculated-performance-series': 'performance',
  'calculated-activity-series': 'value',
  'calculated-benchmark-performance-series': 'performance',
  'calculated-market-value-series': 'value'
}

// Walk a subtree and collect every series node. Returns nodes in the
// order they're discovered so positional fallback (when id is missing)
// stays stable.
function collectSeriesNodes (node, out = []) {
  if (!node || typeof node !== 'object') return out
  if (SERIES_TYPES.has(node.$type) && Array.isArray(node.series)) {
    out.push(node)
  }
  if (Array.isArray(node.calculations)) {
    for (const child of node.calculations) {
      collectSeriesNodes(child, out)
    }
  }
  return out
}

// Local tree-walk for calculated-group nodes used to be inlined here;
// it now lives in `utilityPanel/lib/findRowByIdentity.js` as
// `findGroupByIdentityInTree`. Same id-then-name semantics, shared
// with `useAllocationQuery`'s unwrap.

const DAY_MS = 24 * 60 * 60 * 1000
const DAYS_TO_FREQUENCY = [
  // Threshold (days) -> resolved frequency. Inferred from the gap
  // between consecutive points' endDate. Used when the user kept the
  // `Automatic` setting so the subjectLine can surface what Orion
  // actually picked.
  { maxDays: 2, frequency: 'Daily' },
  { maxDays: 10, frequency: 'Weekly' },
  { maxDays: 45, frequency: 'Monthly' },
  { maxDays: 100, frequency: 'Quarterly' },
  { maxDays: 400, frequency: 'Annually' }
]

function inferFrequency (points) {
  if (!Array.isArray(points) || points.length < 2) return null
  const a = points[0]?.endDate
  const b = points[1]?.endDate
  if (!a || !b) return null
  const ta = new Date(a).getTime()
  const tb = new Date(b).getTime()
  if (!Number.isFinite(ta) || !Number.isFinite(tb)) return null
  const days = Math.abs(tb - ta) / DAY_MS
  for (const rule of DAYS_TO_FREQUENCY) {
    if (days <= rule.maxDays) return rule.frequency
  }
  return 'Annually'
}

// Warn-once dedupe: a single transform call can produce many series
// nodes, and the shape-mismatch warnings would spam the console without
// adding new info on each repeat. We track which (seriesType, key)
// combos have already warned in this module's lifetime.
const warnedValueFieldKeys = new Set()
const warnedPositionalNodes = new Set()

function nodeToSeries (seriesNode) {
  const valueField = SERIES_VALUE_FIELD[seriesNode.$type] || 'value'
  const rawSeries = seriesNode.series || []
  const points = rawSeries.map(entry => ({
    startDate: entry.startDate,
    endDate: entry.endDate,
    // Natural unit -- performance kept as decimal (0.0123 -> 1.23%),
    // activity kept as currency. effectiveFormat handles rendering.
    value: typeof entry[valueField] === 'number' ? entry[valueField] : null
  }))

  // L4: if every point came back null on the expected field but the
  // first raw entry has numeric data under a different key, Orion may
  // have changed the response shape. Warn once per (seriesType,
  // expected, observed) so the next contributor sees it without
  // flooding the console.
  if (points.length > 0 && points.every(p => p.value == null) && rawSeries[0]) {
    const observedNumericFields = Object.keys(rawSeries[0]).filter(k => (
      k !== valueField &&
      typeof rawSeries[0][k] === 'number' &&
      k !== 'startDate' && k !== 'endDate'
    ))
    if (observedNumericFields.length > 0) {
      const warnKey = `${seriesNode.$type}|${valueField}|${observedNumericFields.join(',')}`
      if (!warnedValueFieldKeys.has(warnKey)) {
        warnedValueFieldKeys.add(warnKey)
        // eslint-disable-next-line no-console
        console.warn(
          `[transformSeriesResponse] Expected '${valueField}' on ${seriesNode.$type} entries but every point came back null. Numeric fields actually present on the first entry: ${observedNumericFields.join(', ')}. Did the Orion response shape change?`
        )
      }
    }
  }

  return {
    points,
    resolvedFrequency: inferFrequency(points),
    seriesType: seriesNode.$type
  }
}

/**
 * @param {object} response -- raw `useOrionDomainQuery` data ({ data: { calculations: [...] } })
 * @param {object} [options]
 * @param {{ name: string, groupEntityId: number|null }} [options.unwrap] --
 *   identity of the wrapper group whose series we should chart. Set for
 *   virtual-grouping row scope where the filter doesn't actually
 *   constrain the response and we need to disambiguate among siblings.
 * @returns {{
 *   seriesByCalcId: Object<string, { points, resolvedFrequency, seriesType }>,
 *   seriesNodes: Array  -- discovery-ordered nodes; useful for positional fallback
 * }}
 */
export function transformSeriesResponse (response, { unwrap = null } = {}) {
  if (!response) return { seriesByCalcId: {}, seriesNodes: [] }
  const root = response.data || response

  let searchRoot = { calculations: root?.calculations }
  if (unwrap) {
    const group = findGroupByIdentityInTree(searchRoot, unwrap)
    if (group) {
      searchRoot = group
    }
    // Fall through to the unscoped search if no match -- defensive,
    // shouldn't happen for well-formed requests but keeps the chart
    // from going blank when only the unwrap hint is stale.
  }

  const nodes = collectSeriesNodes(searchRoot)
  const seriesByCalcId = {}
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i]
    // L5: we always emit `trend_N` ids upstream (`buildSeriesCalc`),
    // so a missing `id` here means the request/response shape changed
    // out from under the calc-id round-trip. Warn once per node type
    // so the next contributor sees it; series silently disappear
    // from `seriesByCalcId` lookups otherwise because the calling
    // tool's `calcIds[i]` won't match `__pos_${i}`.
    if (!node.id) {
      const warnKey = `${node.$type}|${i}`
      if (!warnedPositionalNodes.has(warnKey)) {
        warnedPositionalNodes.add(warnKey)
        // eslint-disable-next-line no-console
        console.warn(
          `[transformSeriesResponse] Series node at position ${i} ($type=${node.$type}) is missing an 'id'; falling back to positional key '__pos_${i}'. The calling tool's calcIds[] mapping will not find this series. Likely a request/response shape change.`
        )
      }
    }
    const key = node.id || `__pos_${i}`
    seriesByCalcId[key] = nodeToSeries(node)
  }
  return { seriesByCalcId, seriesNodes: nodes }
}
